/**
 * Drop Target Helper Utilities
 * 
 * Functions for parsing drop target IDs and extracting column/swimlane info.
 * Handles both standard Kanban and swimlane drop target formats.
 */

/**
 * Extract column ID from various drop target ID formats
 * 
 * Formats supported:
 * - column-drop-{columnId} - Standard column droppable
 * - swimlane-drop-{swimlaneId}-{columnId} - Swimlane column droppable
 * 
 * @param {string} overId - The drop target ID
 * @returns {string|null} The extracted column ID or null
 */
export const extractColumnIdFromDropTarget = (overId) => {
  if (typeof overId === 'string') {
    if (overId.startsWith('column-drop-')) {
      // Standard column droppable: column-drop-{columnId}
      return overId.replace('column-drop-', '');
    } else if (overId.startsWith('swimlane-drop-')) {
      // Swimlane column droppable: swimlane-drop-{swimlaneId}-{columnId}
      const parts = overId.replace('swimlane-drop-', '').split('-');
      // The column ID is the last part (after swimlane ID)
      return parts.slice(1).join('-'); // Handle column IDs that might contain dashes
    }
  }
  return null;
};

/**
 * Find which column a task belongs to
 * @param {string} taskId - The task ID to find
 * @param {Object<string, string[]>} taskIdsByColumn - Map of columnId -> taskIds
 * @returns {string|null} The column ID or null if not found
 */
export const findColumnForTask = (taskId, taskIdsByColumn) => {
  for (const [columnId, taskIds] of Object.entries(taskIdsByColumn)) {
    if (taskIds.includes(taskId)) {
      return columnId;
    }
  }
  return null;
};

/**
 * Get a task's position index in the sorted column list
 * @param {string} taskId - The task ID
 * @param {string} columnId - The column ID
 * @param {Object<string, Array>} tasksByStatus - Map of columnId -> tasks array
 * @returns {number} The position index (or length if not found)
 */
export const getTaskPositionIndex = (taskId, columnId, tasksByStatus) => {
  const tasks = tasksByStatus?.[columnId] || [];
  // Tasks are already sorted by kanbanOrder, so array index IS the effective order
  const index = tasks.findIndex(t => t.id === taskId);
  return index >= 0 ? index : tasks.length;
};

